<template>
  <div class="agent-threads">
    <fs-button size="extra-small" accent class="agent-threads__new" @click="agentChat.newThread()">
      <template #icon><span class="agent-threads__plus" v-html="plusIcon" /></template>
      New
    </fs-button>

    <div class="agent-threads__strip">
      <button
        v-for="thread in agentChat.activeThreads"
        :key="thread.id"
        type="button"
        class="agent-threads__tab"
        :class="{ 'agent-threads__tab--active': thread.id === agentChat.activeThread?.summary.id }"
        :title="`${thread.title}${reachLabel(thread) ? ` · ${reachLabel(thread)}` : ''} · ${ago(thread.lastActivityAt)}`"
        @click="agentChat.openThread(thread.id)"
      >
        <span class="agent-threads__status" :class="`agent-threads__status--${statusKey(thread.status)}`" />
        <span class="agent-threads__title">{{ thread.title }}</span>
        <span v-if="reachesAll(thread)" class="agent-threads__all-brands" v-html="unlockedIcon" />
        <span
          class="agent-threads__archive"
          role="button"
          title="Archive thread"
          @click.stop="agentChat.archive(thread.id)"
          v-html="crossIcon"
        />
      </button>

      <span v-if="agentChat.threadsLoaded && !agentChat.activeThreads.length" class="agent-threads__empty">
        No threads yet
      </span>
    </div>

    <!-- Archived threads keep their transcript and are read-only. Behind a menu rather than in
         the strip: the strip is for what is being worked on. -->
    <template v-if="agentChat.archivedThreads.length">
      <button
        ref="archivedButtonRef"
        type="button"
        class="agent-threads__archived-toggle"
        :class="{ 'agent-threads__archived-toggle--open': archivedOpen }"
        @click="archivedOpen = !archivedOpen"
      >
        Archived
        <span class="agent-threads__count">{{ agentChat.archivedThreads.length }}</span>
        <span class="agent-threads__caret" v-html="caretIcon" />
      </button>

      <fs-popper
        :reference="archivedButtonRef"
        :show="archivedOpen"
        :close-on-outside-click="true"
        placement="bottom-end"
        :offset="[0, 6]"
        animation="none"
        no-styling
        @close="archivedOpen = false"
      >
        <ul class="agent-threads__menu">
          <li v-for="thread in agentChat.archivedThreads" :key="thread.id">
            <button type="button" class="agent-threads__menu-item" @click="openArchived(thread.id)">
              <span class="agent-threads__menu-title">{{ thread.title }}</span>
              <span class="agent-threads__menu-meta">
                <span v-if="reachLabel(thread)">{{ reachLabel(thread) }} · </span>{{ ago(thread.lastActivityAt) }}
              </span>
            </button>
          </li>
        </ul>
      </fs-popper>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { formatDistanceToNow } from 'date-fns';
import { AgentThreadStatus, type AgentThreadSummaryDto } from '@/ApiClient';
import { parseServerUtcDate } from '@lib/formatters';
import { useAgentChat } from '@/store/agent-chat';
import { useUser } from '@/store/user';
import plusIcon from '@icons/other/plus-icon.svg';
import crossIcon from '@icons/other/ico-cross.svg';
import caretIcon from '@icons/other/caret-down.svg';
import unlockedIcon from '@icons/miscellaneous/lock-unlocked.svg';
import { usePlmBrands } from '@lib/brand-switch';
import FsButton from '@components/buttons/fs-button.vue';
import FsPopper from '@components/poppers/fs-popper.vue';

const agentChat = useAgentChat();

const archivedOpen = ref(false);
const archivedButtonRef = ref<HTMLElement>();
const openArchived = (threadId: number) => {
  agentChat.openThread(threadId);
  archivedOpen.value = false;
};

const statusKey = (status: AgentThreadStatus) =>
  ({
    [AgentThreadStatus.Idle]: 'idle',
    [AgentThreadStatus.Running]: 'running',
    [AgentThreadStatus.WaitingForApproval]: 'waiting',
    [AgentThreadStatus.Failed]: 'failed',
  })[status] ?? 'idle';

const brandName = (brandId: number | undefined) => {
  const companies = useUser().companies as { brandID?: number; name?: string }[];
  return companies.find((c) => c.brandID === brandId)?.name ?? '';
};

// Marked only where it tells the user something: with one brand, every thread reaches all of them.
const plmBrands = usePlmBrands();
const reachesAll = (thread: AgentThreadSummaryDto) => thread.lockedBrandId == null && plmBrands.value.length > 1;
const reachLabel = (thread: AgentThreadSummaryDto) =>
  reachesAll(thread) ? 'All brands' : brandName(thread.lockedBrandId ?? thread.lastBrandId);

const ago = (date: string) => formatDistanceToNow(parseServerUtcDate(date), { addSuffix: true });
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.agent-threads {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.8rem 2rem;
  border-bottom: 1px solid $c-hairline;
  background: $c-canvas;

  &__new {
    flex: none;
  }

  &__plus {
    display: flex;

    :deep(svg) {
      width: 1.2rem;
      height: 1.2rem;
      fill: currentColor;
    }
  }

  // The strip scrolls rather than wrapping: the bar has to stay one row tall however many
  // threads there are, or it eats the transcript. The scrollbar is hidden because a visible
  // one takes height from a bar this short and leaves the tabs looking off-centre — the fade
  // on the trailing edge is what says there is more.
  &__strip {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    mask-image: linear-gradient(to right, #000 calc(100% - 2.4rem), transparent);

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__tab {
    flex: none;
    max-width: 22rem;
    display: flex;
    align-items: center;
    gap: 0.7rem;
    height: 3rem;
    padding: 0 0.6rem 0 1rem;
    border: 1px solid transparent;
    border-radius: $br-pill;
    background: $c-surface-muted;
    cursor: pointer;
    font: inherit;
    font-size: 1.2rem;
    color: $c-ink-soft;
    transition:
      background $transition-fast ease,
      color $transition-fast ease;

    &:hover {
      color: $c-ink;
    }

    &--active {
      background: $c-accent-tint;
      border-color: rgba($c-ink, 0.12);
      color: $c-ink;
      font-weight: 600;
    }
  }

  &__status {
    flex: none;
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 50%;
    background: $c-border-gray;

    &--running {
      background: $c-blue;
      animation: agent-threads-pulse 1.2s ease-in-out infinite;
    }

    &--waiting {
      background: $c-accent-deep;
    }

    &--failed {
      background: $c-red;
    }
  }

  &__title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__all-brands {
    flex: none;
    display: flex;

    :deep(svg) {
      width: 1.1rem;
      height: 1.1rem;
      fill: rgba($c-ink, 0.45);
    }
  }

  // Takes no room until wanted, so a tab's width is its title rather than its buttons.
  &__archive {
    flex: none;
    display: flex;
    width: 0;
    overflow: hidden;
    opacity: 0;
    transition:
      opacity $transition-fast ease,
      width $transition-fast ease;

    :deep(svg) {
      width: 0.8rem;
      height: 0.8rem;
      fill: rgba($c-ink, 0.5);
    }

    &:hover :deep(svg) {
      fill: $c-ink;
    }
  }

  &__tab:hover &__archive,
  &__tab--active &__archive {
    width: 1.4rem;
    opacity: 1;
  }

  &__empty {
    font-size: 1.2rem;
    color: rgba($c-ink, 0.45);
  }

  &__archived-toggle {
    flex: none;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    height: 3rem;
    padding: 0 1rem;
    border: none;
    border-radius: $br-pill;
    background: none;
    cursor: pointer;
    font: inherit;
    font-size: 1.2rem;
    color: $c-ink-soft;

    &:hover,
    &--open {
      background: $c-surface-muted;
      color: $c-ink;
    }
  }

  &__count {
    color: rgba($c-ink, 0.45);
  }

  &__caret {
    display: flex;

    :deep(svg) {
      width: 0.9rem;
      height: 0.9rem;
      fill: currentColor;
    }
  }

  &__menu {
    width: 30rem;
    max-height: 36rem;
    overflow-y: auto;
    margin: 0;
    padding: 0.6rem 0;
    list-style: none;
    border-radius: $br-very-heavy;
    background: $c-surface;
    box-shadow: $shadow-lifted;
  }

  &__menu-item {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    width: 100%;
    padding: 0.8rem 1.4rem;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    font: inherit;

    &:hover {
      background: $c-surface-muted;
    }
  }

  &__menu-title {
    font-size: 1.3rem;
    color: $c-ink-soft;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__menu-meta {
    font-size: 1.1rem;
    color: rgba($c-ink, 0.45);
  }
}

@keyframes agent-threads-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
</style>
